/**
 * TeamEmailDomainsSection: the email domains whose users join a team on
 * sign-in (TRA-823), set by staff. One comma-separated field, as in the
 * create dialog. A malformed entry is caught here before the save; the
 * API refuses a public provider with a 400, and every save adds the
 * existing users of the saved domains who are not members yet.
 *
 * The saved domains are the team prop. Until staff type, the field shows
 * them, so a refetch of the team list (another tab's save, a focus
 * refetch) moves an untouched form along with it. Typing starts an edit,
 * which remembers the set it started from and sends it with the save:
 * the API answers 409 when the stored set has since moved, and the form
 * says so rather than overwriting the newer save. A save that fails any
 * other way may have stored the set before its backfill failed, so the
 * edit stays sendable until a save succeeds or staff discard it. A save
 * writes the API's answer into the cached team list and ends the edit.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { customFetch, getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type {
  ImpersonatableTeam,
  TeamListResponse,
  UpdateTeamEmailDomainsRequest,
} from '@/api/generated/model'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { INTERNAL_TEAMS_QUERY_KEY } from '@/hooks/useInternalTeams'

import {
  editHasChanges,
  formatDomains,
  malformedDomain,
  parseDomains,
  type DomainsEdit,
} from '../emailDomains'
import type { TeamConfigSectionProps } from './sections'

export function TeamEmailDomainsSection({
  section,
  team,
}: TeamConfigSectionProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const [edit, setEdit] = useState<DomainsEdit | null>(null)
  // The API's answer to the last save. Any edit retires it: the next save
  // is a new question.
  const [saveError, setSaveError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (body: UpdateTeamEmailDomainsRequest) =>
      customFetch<ImpersonatableTeam>(`/api/internal/teams/${team.id}/email-domains`, {
        method: 'PUT',
        data: body,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData<TeamListResponse>(INTERNAL_TEAMS_QUERY_KEY, (previous) =>
        previous === undefined
          ? previous
          : { teams: previous.teams.map((row) => (row.id === saved.id ? saved : row)) },
      )
      setEdit(null)
      toast.success('Email domains saved')
    },
    onError: (error: unknown) => {
      const status = getApiErrorStatus(error)
      const detail = getApiErrorDetail(error) ?? 'Failed to save the email domains'
      if (status === 409) {
        // Fetch the newer save, so Discard loads it.
        void queryClient.invalidateQueries({ queryKey: INTERNAL_TEAMS_QUERY_KEY })
      }
      if (status === 400 || status === 409) {
        setSaveError(detail)
      } else {
        setEdit((previous) => (previous === null ? previous : { ...previous, resend: true }))
        toast.error(detail)
      }
    },
  })

  const field = edit === null ? formatDomains(team.email_domains) : edit.text
  const domains = parseDomains(field)
  const malformed = malformedDomain(domains)
  const dirty = editHasChanges(edit, team.email_domains)
  const canSubmit = !saveMutation.isPending && dirty && malformed === null

  const discard = (): void => {
    setEdit(null)
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (edit === null || !canSubmit) return
    setSaveError(null)
    saveMutation.mutate({ email_domains: domains, expected_email_domains: edit.startedFrom })
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <form onSubmit={handleSubmit}>
        <SettingsSection
          title="Auto-join domains"
          description="A user who signs in with one of these domains passes the sign-up gate and joins this team. Adding a domain also adds its existing users now. Removing a domain keeps its members, and the team owner can then remove them."
        >
          <div className="max-w-md space-y-2">
            <Label htmlFor="team-email-domains">Email domains</Label>
            <Input
              id="team-email-domains"
              value={field}
              placeholder="nollahealth.com, nolla.fi"
              disabled={saveMutation.isPending}
              aria-invalid={malformed !== null}
              aria-describedby="team-email-domains-help"
              onChange={(e) => {
                const text = e.target.value
                setEdit((previous) => ({
                  text,
                  startedFrom: previous?.startedFrom ?? team.email_domains,
                  resend: previous?.resend ?? false,
                }))
                setSaveError(null)
              }}
            />
            <p id="team-email-domains-help" className="text-xs text-muted-foreground">
              Comma-separated. Another team can claim the same domain. Leave empty for an
              invite-only team.
            </p>
          </div>
        </SettingsSection>

        <SettingsFormFooter
          dirty={dirty}
          error={malformed === null ? saveError : malformedError(malformed)}
        >
          <Button
            type="button"
            variant="outline"
            disabled={edit === null || saveMutation.isPending}
            onClick={discard}
          >
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </form>
    </div>
  )
}

function malformedError(entry: string): string {
  return `${entry} is not an email domain. Enter the part after the @, such as acme.com.`
}
